import { ChevronLeft, Download, File, Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  FetchEventDetailAction,
  FetchEventCollaborationAction,
  PublishEventAction,
} from "../../Store/Actions/EventMasterActions";
import { EventMasterActionConst } from "../../Store/Constants/EventMasterConstants";

const volunteers = [
  { id: 1, name: "Emily Carter", role: "Team Lead", organization: "Green Earth Initiative", hours: 15 },
  { id: 2, name: "David Lee", role: "Volunteer", organization: "Local High School", hours: 10 },
  { id: 3, name: "Maria Rodriguez", role: "Volunteer", organization: "Community Center", hours: 8 },
];

const MainEventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const processedRef = useRef(null);

  const { detail, loader, detailError, collaboration, actionSuccess, actionError } = useSelector(
    (state) => state.eventMaster
  );

  useEffect(() => {
    if (id) {
      processedRef.current = null;
      dispatch(FetchEventDetailAction(id));
      dispatch(FetchEventCollaborationAction(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (detailError) {
      toast.error(detailError.message || "Failed to load event.");
    }
  }, [detailError]);

  useEffect(() => {
    if (actionSuccess && actionSuccess.statusCode === 200 && !processedRef.current) {
      processedRef.current = actionSuccess;
      toast.success(actionSuccess.message || "Action completed successfully.");
      dispatch({ type: EventMasterActionConst.CLEAR_EVENT_ACTION_STATE });
      if (actionSuccess.message?.toLowerCase().includes("publish")) {
        dispatch(FetchEventDetailAction(id));
      }
    }
  }, [actionSuccess, dispatch, id]);

  useEffect(() => {
    if (actionError) {
      toast.error(actionError.message || "Action failed. Please try again.");
      dispatch({ type: EventMasterActionConst.CLEAR_EVENT_ACTION_STATE });
    }
  }, [actionError, dispatch]);

  const handlePublish = () => {
    if (!id) return;
    dispatch(PublishEventAction(id));
  };

  const handleEdit = () => {
    toast("Edit functionality coming soon.", { icon: "ℹ️" });
  };

  if (loader && !detail) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="p-6 text-sm font-popinsRegular text-[#61758A]">
        Event not found.
      </div>
    );
  }

  const attachments = Array.isArray(detail.attachments) ? detail.attachments : [];

  return (
    <div className="w-[95%] mx-auto">
      <div className="mt-8">
        <div
          className="flex items-center gap-x-1 ml-[-7px] cursor-pointer"
          onClick={() => navigate("/dashboard/event-master")}
        >
          <ChevronLeft size={22} />
          <h1 className="font-popinsSemiBold text-xl lg:text-2xl">{detail.title}</h1>
        </div>
        <p className="font-popinsMedium text-textColor text-xs pt-2">
          Created by {detail.postedBy} • {detail.status}
        </p>
      </div>

      <div className="mt-6 lg:w-[85%]">
        <p className="font-popinsSemiBold text-lg">Description</p>
        <p className="font-popinsRegular text-sm mt-2">
          {detail.description || "No description provided."}
        </p>
      </div>

      <div className="mt-6 lg:w-[85%]">
        <p className="font-popinsSemiBold text-lg border-b border-border pb-6">
          Key Information
        </p>
        <div className="flex gap-x-12 pt-6 pb-10">
          <div>
            <p className="font-popinsMedium text-[#61758A] text-base">Date</p>
            <p className="font-popinsRegular text-sm">{detail.date}</p>
          </div>
          <div>
            <p className="font-popinsMedium text-[#61758A] text-base">Time</p>
            <p className="font-popinsRegular text-sm">{detail.time}</p>
          </div>
        </div>
        <div className="w-44 h-[1px] bg-border"></div>
        <div className="pt-7">
          <p className="font-popinsMedium text-[#61758A] text-base">Location</p>
          <p className="font-popinsRegular text-sm">{detail.location}</p>
        </div>
      </div>

      {/* Volunteers — hardcoded per design */}
      <div className="lg:w-[85%]">
        <h3 className="font-popinsSemiBold mt-8 mb-3 text-xl">Volunteers</h3>
        <div className="overflow-x-auto rounded-md border border-gray-300 panelScroll">
          <table className="min-w-full bg-white">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-sm font-popinsRegular">Name</th>
                <th className="px-4 py-3 text-left text-sm font-popinsRegular">Role</th>
                <th className="px-4 py-3 text-left text-sm font-popinsRegular">Organization</th>
                <th className="px-4 py-3 text-left text-sm font-popinsRegular">Hours Served</th>
              </tr>
            </thead>
            <tbody>
              {volunteers.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="px-4 py-4 text-sm font-popinsRegular">{item.name}</td>
                  <td className="px-4 py-4 text-[#61758A] text-sm font-popinsRegular">{item.role}</td>
                  <td className="px-4 py-4 text-[#61758A] text-sm font-popinsRegular">{item.organization}</td>
                  <td className="px-4 py-4 text-sm font-popinsRegular">{item.hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Resources — from real attachments */}
      <div className="lg:w-[85%]">
        <h3 className="font-popinsSemiBold mt-8 mb-3 text-xl">Resources</h3>
        {attachments.length === 0 ? (
          <p className="font-popinsRegular text-sm text-[#61758A]">No attachments.</p>
        ) : (
          <div className="flex flex-col gap-y-4">
            {attachments.map((file, i) => (
              <div key={i} className="flex justify-between items-center">
                <div className="flex items-center gap-x-3">
                  <div className="bg-gray-200 rounded-md p-3">
                    <File size={18} />
                  </div>
                  <p className="font-popinsRegular text-base">{file.name || `Attachment ${i + 1}`}</p>
                </div>
                <a href={file.url} target="_blank" rel="noreferrer" download>
                  <Download size={20} className="cursor-pointer" />
                </a>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Collaboration Requests */}
      <div className="lg:w-[85%]">
        <h3 className="font-popinsSemiBold mt-8 mb-3 text-xl">Collaboration Requests</h3>
        {!collaboration ? (
          <p className="font-popinsRegular text-sm text-[#61758A]">No collaboration requests.</p>
        ) : collaboration.recipients?.length === 0 ? (
          <p className="font-popinsRegular text-sm text-[#61758A]">No recipients yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-gray-300 panelScroll">
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-popinsRegular">Resource</th>
                  <th className="px-4 py-3 text-left text-sm font-popinsRegular">Quantity</th>
                  <th className="px-4 py-3 text-left text-sm font-popinsRegular">Assign To</th>
                  <th className="px-4 py-3 text-left text-sm font-popinsRegular">Status</th>
                </tr>
              </thead>
              <tbody>
                {collaboration.recipients.flatMap((recipient) =>
                  recipient.tasks.map((task, ti) => (
                    <tr key={`${recipient.id}-${ti}`} className="border-t">
                      <td className="px-4 py-4 text-sm font-popinsRegular">{task.taskName}</td>
                      <td className="px-4 py-4 text-sm font-popinsRegular">{task.countNeeded}</td>
                      <td className="px-4 py-4 text-sm font-popinsRegular text-[#61758A]">
                        {recipient.nonprofit?.organizationName ?? "—"}
                      </td>
                      <td className="px-4 py-4 text-sm font-popinsRegular">{task.status}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Admin Actions */}
      <div className="lg:w-[85%] mb-6">
        <h3 className="font-popinsSemiBold mt-8 text-xl">Admin Actions</h3>
        <div className="flex gap-x-5 mt-5">
          <button
            onClick={handleEdit}
            className="relative overflow-hidden py-[10px] px-8 bg-[#F0F2F5] text-black font-popinsSemiBold text-sm rounded-md shadow-lg
           transform transition duration-300 ease-in-out
           hover:bg-[#F0F2F5] hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)]
           active:scale-95 active:shadow-inner focus:outline-none"
          >
            <span>Edit Event</span>
          </button>
          <button
            onClick={handlePublish}
            disabled={!detail.isDraft}
            className="relative overflow-hidden py-[10px] px-8 bg-themeColor text-white font-popinsSemiBold text-sm rounded-md shadow-lg
           transform transition duration-300 ease-in-out
           hover:bg-themeColor hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)]
           active:scale-95 active:shadow-inner focus:outline-none
           disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            <span>Publish Event</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MainEventDetail;
